import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { ArrowRight, Loader2, Dna, Compass, CalendarCheck, ShieldCheck } from "lucide-react";
import { api, apiError, track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { firstName } from "@/lib/name";
import { acquisitionForDraft } from "@/lib/acquisition";
import { clearDraft } from "@/lib/assessment";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

const STEPS = [
  { icon: Dna, title: "Your Career DNA", sub: "who you are, professionally" },
  { icon: Compass, title: "Your top career matches, explained", sub: "not just a percentage" },
  { icon: CalendarCheck, title: "A real plan to get ready for it", sub: "one doable step at a time" },
];

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(firstName(user));
  const [stage, setStage] = useState("name");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (stage === "welcome") {
      confetti({ particleCount: 90, spread: 70, origin: { y: 0.35 }, colors: ["#4F46E5", "#8B5CF6", "#F59E0B", "#10B981"] });
      track("welcome_viewed");
    }
  }, [stage]);

  const submitName = async (e) => {
    e.preventDefault();
    const clean = name.trim().split(/\s+/)[0];
    if (!clean) return;
    setLoading(true);
    try {
      const { data } = await api.post("/assessment/draft", { first_name: clean, acquisition: acquisitionForDraft() });
      clearDraft();
      sessionStorage.setItem("nxt_assessment_token", data.token);
      setName(clean);
      setStage("welcome");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col px-5 py-8">
        <Logo className="mb-6" />
        <AnimatePresence mode="wait">
          {stage === "name" ? (
            <motion.form key="name" onSubmit={submitName} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4 }}
              className="my-auto" data-testid="welcome-name-step">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">One quick thing</div>
              <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">What should we call you?</h1>
              <p className="mt-2 text-muted-foreground">Just your first name — that's all we need to make this yours.</p>
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={30} placeholder="Your first name" data-testid="welcome-name-input"
                className="mt-8 w-full rounded-2xl border border-input bg-card px-5 py-4 font-display text-2xl font-bold outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/15" />
              <button type="submit" disabled={loading || !name.trim()} data-testid="welcome-name-submit"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-indigo-600 py-4 font-bold text-white transition-all hover:bg-indigo-700 active:scale-[0.98] disabled:opacity-50">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4" /></>}
              </button>
            </motion.form>
          ) : (
            <motion.div key="welcome" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="my-auto" data-testid="welcome-screen">
              <motion.h1 initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                className="font-display text-5xl font-extrabold tracking-tight [overflow-wrap:anywhere] sm:text-6xl" data-testid="welcome-greeting">Hi {name} <span className="inline-block origin-[70%_70%] animate-[wave_1.6s_ease-in-out_1]">👋</span></motion.h1>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="mt-4 text-base text-muted-foreground md:text-lg">
                NxT is your personal career operating system — not a quiz, not a chatbot. It figures out what fits you, and builds you a plan to get there.
              </motion.p>
              <div className="mt-7 space-y-3">
                {STEPS.map((s, i) => (
                  <motion.div key={s.title} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.15 }}
                    className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4" data-testid={`welcome-step-${i + 1}`}>
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white"><s.icon className="h-5 w-5" /></div>
                    <div><div className="font-bold">{s.title}</div><div className="text-sm text-muted-foreground">{s.sub}</div></div>
                  </motion.div>
                ))}
              </div>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-5 flex items-center gap-2 text-xs text-muted-foreground" data-testid="welcome-trust">
                <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" /> Takes a few minutes. Your answers are private and never shared with employers or recruiters.
              </motion.p>
              <motion.button initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }} onClick={() => { track("welcome_cta_clicked"); navigate("/assessment"); }} data-testid="welcome-start-btn"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-4 font-bold text-white [overflow-wrap:anywhere] transition-colors hover:bg-indigo-600 active:scale-[0.98]">
                Let's find out — {name}'s Career DNA <ArrowRight className="h-4 w-4" />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
